import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from '../utils/axios';

const LOCK_STORAGE_KEY = 'mb_screen_locked';
const LOCK_CHANNEL_NAME = 'mb_screen_lock_channel';
const ACTIVITY_THROTTLE_MS = 5_000;
const SERVER_POLL_INTERVAL_MS = 30_000;
const ACTIVITY_HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Единый хук блокировки экрана.
 *
 * Архитектура:
 * 1. Серверный lock-state (uam.user_lock_state) — единственный источник истины.
 * 2. BroadcastChannel — мгновенная кросс-вкладочная синхронизация.
 * 3. localStorage events — fallback для старых браузеров.
 * 4. Polling каждые 30 с — кросс-доменная синхронизация (Auth ↔ App).
 * 5. Inactivity detection (mousedown, mousemove, keydown, scroll, touchstart, visibilitychange).
 *    Блокировка ТОЛЬКО при отсутствии действий, а не просто спустя N минут.
 * 6. Cross-tab activity sharing — активность в одной вкладке сбрасывает таймер во всех.
 */
export const useScreenLock = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const isCheckingAuth = useSelector((state) => state.auth.checkingAuth);
  const authReady = isAuthenticated && !isCheckingAuth;

  const [isLocked, setIsLocked] = useState(() => {
    try {
      return localStorage.getItem(LOCK_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [serverConfig, setServerConfig] = useState(null);

  const inactivityTimerRef = useRef(null);
  const lastResetRef = useRef(Date.now());
  const pollTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const channelRef = useRef(null);

  // Refs для доступа к актуальным значениям из stale closures (BroadcastChannel, setTimeout)
  const isLockedRef = useRef(isLocked);
  const isEnabledRef = useRef(false);
  const timeoutMsRef = useRef(0);
  const lockScreenRef = useRef(null);
  const resetTimerRef = useRef(null);

  const isEnabled = serverConfig?.enabled === true;
  const inactivityMinutes = serverConfig?.timeout_minutes || 60;
  const timeoutMs = isEnabled ? (inactivityMinutes * 60 * 1000) : 0;

  isLockedRef.current = isLocked;
  isEnabledRef.current = isEnabled;
  timeoutMsRef.current = timeoutMs;

  // ------- Серверный статус -------
  const fetchStatus = useCallback(async () => {
    if (!authReady) return null;
    try {
      const res = await axios.get('/api/uam/v1/unlock/status', { silent401: true });
      const data = res?.data;
      if (data) {
        setServerConfig(data);
        // Как на backend: блокировка только если включена и is_locked; иначе UI не копит «левый» lock
        const serverLocked = data.is_locked === true && data.enabled !== false;
        if (serverLocked && !isLockedRef.current) {
          setIsLocked(true);
          try { localStorage.setItem(LOCK_STORAGE_KEY, 'true'); } catch {}
        } else if (!serverLocked && isLockedRef.current) {
          setIsLocked(false);
          try { localStorage.setItem(LOCK_STORAGE_KEY, 'false'); } catch {}
        }
      }
      return data;
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401) return null;
      // UAM/прокси недоступен — не оставляем устаревший флаг из localStorage (нельзя снять экран, verify тоже падает)
      if (st === 502 || st === 503 || st === 504) {
        if (isLockedRef.current) {
          setIsLocked(false);
          try { localStorage.setItem(LOCK_STORAGE_KEY, 'false'); } catch {}
        }
      }
      return null;
    }
  }, [authReady]);

  // Первичная загрузка + периодический polling (только после checkAuth / markbase_auth_ready)
  useEffect(() => {
    if (!authReady) return;
    fetchStatus();
    pollTimerRef.current = setInterval(fetchStatus, SERVER_POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [authReady, fetchStatus]);

  // ------- Блокировка -------
  // Блокировка разрешена только если:
  // - пользователь авторизован
  // - screen_lock_enabled = true в настройках (isEnabledRef.current)
  //   ИЛИ причина — серверная (server_423), чтобы принудительная блокировка всегда работала
  const lockScreen = useCallback(async (reason = 'manual') => {
    if (!isAuthenticated) return;
    // Если настройка отключена — блокировать нельзя (кроме принудительного server-push)
    if (!isEnabledRef.current && reason !== 'server_423') return;
    setIsLocked(true);
    try { localStorage.setItem(LOCK_STORAGE_KEY, 'true'); } catch {}
    try { channelRef.current?.postMessage({ type: 'LOCK', reason }); } catch {}
    try {
      await axios.post('/api/uam/v1/unlock/lock', { reason });
    } catch {}
  }, [isAuthenticated]);

  lockScreenRef.current = lockScreen;

  // ------- Разблокировка -------
  const unlockScreen = useCallback(() => {
    setIsLocked(false);
    try {
      localStorage.setItem(LOCK_STORAGE_KEY, 'false');
      localStorage.removeItem('mb_lock_reason');
    } catch {}
    try { channelRef.current?.postMessage({ type: 'UNLOCK' }); } catch {}
    try { document.body.style.overflow = ''; } catch {}
    try {
      const main = document.querySelector('.mb-layout__main--locked');
      if (main) main.classList.remove('mb-layout__main--locked');
    } catch {}
    fetchStatus();
  }, [fetchStatus]);

  // ------- Inactivity timer (через ref — нет stale closure) -------
  const resetInactivityTimer = useCallback(() => {
    if (!isEnabledRef.current || isLockedRef.current || timeoutMsRef.current <= 0) return;
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      lockScreenRef.current?.('inactivity');
    }, timeoutMsRef.current);
  }, []);

  resetTimerRef.current = resetInactivityTimer;

  // ------- BroadcastChannel (регистрируется один раз, обращается к актуальным функциям через refs) -------
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      const ch = new BroadcastChannel(LOCK_CHANNEL_NAME);
      channelRef.current = ch;

      ch.onmessage = (e) => {
        const msg = e.data;
        if (!msg || typeof msg !== 'object') return;

        if (msg.type === 'LOCK') {
          setIsLocked(true);
          try { localStorage.setItem(LOCK_STORAGE_KEY, 'true'); } catch {}
        } else if (msg.type === 'UNLOCK') {
          setIsLocked(false);
          try { localStorage.setItem(LOCK_STORAGE_KEY, 'false'); } catch {}
        } else if (msg.type === 'ACTIVITY') {
          lastResetRef.current = Date.now();
          resetTimerRef.current?.();
        }
      };

      return () => {
        ch.close();
        channelRef.current = null;
      };
    } catch {
      return undefined;
    }
  }, []);

  // ------- localStorage fallback для кросс-вкладочной синхронизации -------
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== LOCK_STORAGE_KEY) return;
      setIsLocked(e.newValue === 'true');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // После свежего входа (login/register/guest) auth сбрасывает mb_screen_locked — подхватываем в state
  useEffect(() => {
    const h = () => {
      setIsLocked(false);
    };
    window.addEventListener('markbase_screen_lock_clear_stale', h);
    return () => window.removeEventListener('markbase_screen_lock_clear_stale', h);
  }, []);

  // ------- Inactivity detection (сбрасывает таймер при любом действии) -------
  useEffect(() => {
    if (!isAuthenticated || !isEnabled || isLocked || timeoutMs <= 0) return;

    resetInactivityTimer();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < ACTIVITY_THROTTLE_MS) return;
      lastResetRef.current = now;
      resetInactivityTimer();
      try { channelRef.current?.postMessage({ type: 'ACTIVITY' }); } catch {}
    };

    const handleVisibility = () => {
      if (!document.hidden) handleActivity();
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      events.forEach(ev => window.removeEventListener(ev, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, isEnabled, isLocked, timeoutMs, resetInactivityTimer]);

  // ------- Heartbeat активности на сервер (предотвращает серверный авто-локаут) -------
  useEffect(() => {
    if (!isAuthenticated || !isEnabled || isLocked) return;
    heartbeatTimerRef.current = setInterval(() => {
      axios.post('/api/uam/v1/unlock/activity').catch(() => {});
    }, ACTIVITY_HEARTBEAT_INTERVAL_MS);
    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    };
  }, [isAuthenticated, isEnabled, isLocked]);

  // ------- Ручная блокировка из Header / ProfileDropdown -------
  // Событие игнорируется, если screen_lock_enabled = false в настройках пользователя.
  useEffect(() => {
    const handler = () => {
      if (isEnabledRef.current) {
        lockScreenRef.current?.('manual');
      }
    };
    window.addEventListener('markbase_screen_lock', handler);
    return () => window.removeEventListener('markbase_screen_lock', handler);
  }, []);

  return {
    isLocked,
    isEnabled,
    lockScreen,
    unlockScreen,
    serverConfig,
    fetchStatus,
  };
};

export default useScreenLock;
